import { v4 as uuidv4 } from "uuid";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

import { STORAGE_KEYS } from "../app_constants";

export interface DiagramEntry {
  id: string;
  name: string;
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  created: number;
  updated?: number;
  thumbnail: string;
}

export const loadDiagrams = (): DiagramEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_DIAGRAMS);
    if (raw) {
      return (JSON.parse(raw) as DiagramEntry[]).map((d) => ({
        ...d,
        thumbnail: d.thumbnail || "",
      }));
    }
  } catch (e) {
    console.error(e);
  }
  return [];
};

const writeDiagrams = (list: DiagramEntry[]) => {
  localStorage.setItem(
    STORAGE_KEYS.LOCAL_STORAGE_DIAGRAMS,
    JSON.stringify(list),
  );
};

// 同一IDがあれば上書き、無ければ新規作成
export const upsertDiagram = (
  partial: Omit<DiagramEntry, "id" | "created"> & { id?: string },
): DiagramEntry => {
  const diagrams = loadDiagrams();
  const now = Date.now();

  if (partial.id) {
    const idx = diagrams.findIndex((d) => d.id === partial.id);
    if (idx >= 0) {
      const updated = {
        ...diagrams[idx],
        ...partial,
        id: partial.id,
        updated: now,
      };
      diagrams[idx] = updated;
      writeDiagrams(diagrams);
      return updated;
    }
  }

  const created: DiagramEntry = {
    id: partial.id ?? uuidv4(),
    name: partial.name ?? `Diagram ${new Date().toLocaleString()}`,
    elements: partial.elements ?? [],
    appState: partial.appState,
    thumbnail: partial.thumbnail,
    created: now,
    updated: now,
  };
  diagrams.push(created);
  writeDiagrams(diagrams);
  return created;
};

export const getDiagram = (id: string): DiagramEntry | undefined => {
  return loadDiagrams().find((d) => d.id === id);
};

export const renameDiagram = (id: string, name: string) => {
  const diagrams = loadDiagrams();
  const index = diagrams.findIndex((d) => d.id === id);
  if (index >= 0) {
    diagrams[index] = { ...diagrams[index], name };
    try {
      localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_DIAGRAMS,
        JSON.stringify(diagrams),
      );
    } catch (e) {
      console.error(e);
    }
  }
};

export const deleteDiagram = (id: string) => {
  const diagrams = loadDiagrams().filter((d) => d.id !== id);
  try {
    localStorage.setItem(
      STORAGE_KEYS.LOCAL_STORAGE_DIAGRAMS,
      JSON.stringify(diagrams),
    );
  } catch (e) {
    console.error(e);
  }
};
